import { Dialog } from '../ui/overlays';
import { useMedia } from '../ui/responsive';
import { useEffect, useState } from 'react';
import { hexA } from '@scribbleplot/shared';
import { color, shadow } from '../tokens';
import { useBoard } from './store';

/**
 * Pinned comment threads.
 *
 * Pins are anchored in world space but counter-scaled, so they stay legible at
 * any zoom — a comment marker that shrinks to a speck when you zoom out is
 * useless. The open thread renders next to its pin rather than in a side panel,
 * which keeps the discussion attached to what it is about.
 */

export function CommentPins({ accent, scale, authorName }: { accent: string; scale: number; authorName: string }) {
  const phone = useMedia('(max-width: 767px)');
  const coarse = useMedia('(pointer: coarse)');
  const comments = useBoard((s) => s.doc.comments);
  const canEdit = useBoard((s) => s.canEdit);
  const addCommentMsg = useBoard((s) => s.addCommentMsg);
  const resolveComment = useBoard((s) => s.resolveComment);

  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const open = comments.find((c) => c.id === openId) ?? null;

  // A freshly placed pin opens straight away so the author can type.
  useEffect(() => {
    const last = comments[comments.length - 1];
    if (last && !last.msgs.length && last.author === authorName) {
      setOpenId(last.id);
      setDraft('');
    }
  }, [comments.length, authorName, comments]);

  useEffect(() => {
    if (!openId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId]);

  const submit = () => {
    const text = draft.trim();
    if (!text || !open) return;
    addCommentMsg(open.id, authorName, text);
    setDraft('');
  };

  return (
    <>
      {comments.map((comment) => (
        <button
          key={comment.id}
          type="button"
          title={`Comment by ${comment.author}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => {
            setOpenId(comment.id === openId ? null : comment.id);
            setDraft('');
          }}
          style={{
            position: 'absolute',
            left: comment.x,
            top: comment.y,
            width: coarse ? 44 : 27,
            height: coarse ? 44 : 27,
            // Teardrop: the square corner points at what is being discussed.
            borderRadius: '14px 14px 14px 3px',
            background: comment.author === authorName ? accent : '#F49E9E',
            border: `2px solid ${color.bg}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 800,
            color: color.onAccent,
            cursor: 'pointer',
            zIndex: 99_995,
            padding: 0,
            transform: `translate(-2px,-100%) scale(${(1 / scale).toFixed(3)})`,
            transformOrigin: 'bottom left',
            boxShadow: '0 6px 16px rgba(0,0,0,.4)',
          }}
        >
          {comment.author.charAt(0).toUpperCase()}
        </button>
      ))}

      {open && (
        <CommentPanel phone={phone} onClose={() => setOpenId(null)}>
          <div
            onPointerDown={(event) => event.stopPropagation()}
            style={{
              position: 'absolute',
              left: open.x + 14,
              top: open.y + 8,
              width: 252,
              transform: phone ? 'none' : `scale(${(1 / scale).toFixed(3)})`,
              transformOrigin: 'top left',
              background: color.panel,
              border: `1px solid ${color.lineStrong}`,
              borderRadius: 13,
              padding: '11px 12px',
              zIndex: 99_996,
              boxShadow: shadow.panel,
            }}
          >
            {open.msgs.map((msg) => (
              <div key={msg.id} style={{ display: 'flex', gap: 9, padding: '5px 0', alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flex: 'none',
                    background: msg.who === authorName ? accent : '#F49E9E',
                    color: color.onAccent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {msg.who.charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: color.textDim }}>{msg.who}</div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: color.textStrong,
                      lineHeight: 1.45,
                      marginTop: 1,
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              </div>
            ))}

            {!open.msgs.length && (
              <div style={{ fontSize: 12, color: color.textGhost, padding: '2px 0 4px' }}>
                {canEdit ? 'Start the thread…' : 'No replies yet.'}
              </div>
            )}

            {canEdit && (
              <input
                value={draft}
                placeholder="Comment…"
                aria-label="Add a comment"
                autoFocus
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') submit();
                  if (event.key === 'Escape') setOpenId(null);
                }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  marginTop: 6,
                  height: 32,
                  background: color.input,
                  border: `1px solid ${color.lineStrong}`,
                  borderRadius: 8,
                  color: color.text,
                  fontSize: 12.5,
                  padding: '0 10px',
                  outline: 'none',
                }}
              />
            )}

            {canEdit && (
              <button
                className="sp-button sp-primary"
                disabled={!draft.trim()}
                style={{ marginTop: 8 }}
                onClick={submit}
              >
                Post comment
              </button>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9 }}>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => {
                    resolveComment(open.id);
                    setOpenId(null);
                  }}
                  style={{
                    height: 26,
                    padding: '0 11px',
                    borderRadius: 7,
                    border: 'none',
                    background: hexA(accent, 0.16),
                    color: accent,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Resolve
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => setOpenId(null)}
                style={{
                  height: 26,
                  padding: '0 11px',
                  borderRadius: 7,
                  border: 'none',
                  background: 'transparent',
                  color: color.textDim,
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </CommentPanel>
      )}
    </>
  );
}

function CommentPanel({
  phone,
  onClose,
  children,
}: {
  phone: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return phone ? (
    <Dialog title="Comment thread" drawer onClose={onClose} initialFocus="input">
      <div className="sp-editor-drawer-content">{children}</div>
    </Dialog>
  ) : (
    <>{children}</>
  );
}
